/**
 * StatusPill.tsx — colour + glyph + label badge for a Flag.
 *
 * Accessibility: every pill includes BOTH a colour AND a glyph (✓ / ! / ✕ /
 * –) so colour-blind users still get the signal. The descriptive label is
 * exposed via `title` (native tooltip) and `aria-label`.
 *
 * Variants (Phase 2):
 *   - `submission` (default) — On time / Stale / Overdue / Never.
 *   - `npa`        — NPA flag rendered as "Healthy / Watch / Critical / N/A".
 *   - `dq`         — Data-quality flag: "Clean / Spotty / Poor / N/A".
 */

import React from 'react';

import { flagClass, flagIcon, flagLabel } from '../helpers/flag';
import type { Flag } from '../type/api';

export type StatusPillVariant = 'submission' | 'npa' | 'dq';

interface Props {
	flag: Flag;
	/** When true, drop the text label and show only icon + colour. */
	compact?: boolean;
	/** Per-page colour rules (default: `submission`). */
	variant?: StatusPillVariant;
	/** Override the auto-derived label (e.g. show the actual % value). */
	label?: string;
}

const NPA_LABEL: Record<Flag, string> = {
	GREEN: 'Healthy',
	AMBER: 'Watch',
	RED: 'Critical',
	GREY: 'N/A',
};

const DQ_LABEL: Record<Flag, string> = {
	GREEN: 'Clean',
	AMBER: 'Spotty',
	RED: 'Poor',
	GREY: 'N/A',
};

function variantLabel(variant: StatusPillVariant, flag: Flag): string {
	switch (variant) {
		case 'npa':
			return NPA_LABEL[flag];
		case 'dq':
			return DQ_LABEL[flag];
		case 'submission':
		default:
			return flagLabel(flag);
	}
}

export default function StatusPill({ flag, compact = false, variant = 'submission', label }: Props) {
	const cls = flagClass(flag);
	const text = label ?? variantLabel(variant, flag);
	return (
		<span
			className={`status-pill ${cls}`}
			title={text}
			aria-label={`Status: ${text}`}
		>
			<span aria-hidden="true">{flagIcon(flag)}</span>
			{!compact && <span>{text}</span>}
		</span>
	);
}
